import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from "recharts";
import { TrendingUp, TrendingDown, Minus, Clock } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { cn } from "@/lib/utils";

export interface DurationTrendData {
  period: string;
  avg_duration_ms: number;
  min_duration_ms: number;
  max_duration_ms: number;
  execution_count: number;
}

interface ExecutionDurationChartProps {
  data: DurationTrendData[];
  isLoading: boolean;
  title?: string;
  showCard?: boolean;
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload as DurationTrendData;
  
  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-2">
        {format(new Date(data.period), "d MMM HH:mm", { locale: nl })}
      </p>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Gemiddeld:</span>
          <span className="font-mono">{formatDuration(data.avg_duration_ms)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Min:</span>
          <span className="font-mono text-green-600">{formatDuration(data.min_duration_ms)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Max:</span>
          <span className="font-mono text-red-600">{formatDuration(data.max_duration_ms)}</span>
        </div>
        <div className="flex justify-between gap-4 pt-1 border-t">
          <span className="text-muted-foreground">Uitvoeringen:</span>
          <span className="font-medium">{data.execution_count}</span>
        </div>
      </div>
    </div>
  );
};

export const ExecutionDurationChart: React.FC<ExecutionDurationChartProps> = ({
  data,
  isLoading,
  title = "Duur Trend",
  showCard = true,
}) => {
  // Calculate trend
  const getTrend = (): { direction: "up" | "down" | "stable"; percentage: number } => {
    if (data.length < 2) return { direction: "stable", percentage: 0 };
    
    const firstHalf = data.slice(0, Math.floor(data.length / 2));
    const secondHalf = data.slice(Math.floor(data.length / 2));
    
    const firstAvg = firstHalf.reduce((acc, d) => acc + d.avg_duration_ms, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((acc, d) => acc + d.avg_duration_ms, 0) / secondHalf.length;
    
    const change = ((secondAvg - firstAvg) / firstAvg) * 100;
    
    if (Math.abs(change) < 5) return { direction: "stable", percentage: 0 };
    return { 
      direction: change > 0 ? "up" : "down", 
      percentage: Math.abs(Math.round(change)) 
    };
  };

  const trend = getTrend();
  const overallAvg = data.length > 0 
    ? data.reduce((acc, d) => acc + d.avg_duration_ms, 0) / data.length 
    : 0;

  const content = (
    <>
      {isLoading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Laden...</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-muted-foreground">
          Geen data beschikbaar
        </div>
      ) : (
        <>
          {/* Summary row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Gem: <span className="font-mono font-medium text-foreground">{formatDuration(overallAvg)}</span>
              </span>
            </div>
            <div className={cn(
              "flex items-center gap-1 text-sm",
              trend.direction === "up" && "text-red-600",
              trend.direction === "down" && "text-green-600",
              trend.direction === "stable" && "text-muted-foreground"
            )}>
              {trend.direction === "up" && <TrendingUp className="h-4 w-4" />}
              {trend.direction === "down" && <TrendingDown className="h-4 w-4" />}
              {trend.direction === "stable" && <Minus className="h-4 w-4" />}
              <span>
                {trend.direction === "stable" 
                  ? "Stabiel" 
                  : `${trend.percentage}% ${trend.direction === "up" ? "↑" : "↓"}`}
              </span>
            </div>
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="durationGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="period" 
                tickFormatter={(value) => format(new Date(value), "HH:mm")}
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                tickFormatter={(value) => formatDuration(value)}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={45}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine 
                y={overallAvg} 
                stroke="hsl(var(--muted-foreground))" 
                strokeDasharray="3 3" 
                strokeOpacity={0.5}
              />
              <Area
                type="monotone"
                dataKey="avg_duration_ms"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#durationGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};
